import { AbilityProvider } from "@casl/react";
import { Outlet } from "@tanstack/react-router";
import { FC, Suspense, useLayoutEffect, useMemo } from "react";

import { defineAbilityFor } from "@recobra/core";
import { Separator } from "@recobra/ui/separator";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@recobra/ui/sidebar";
import { TooltipProvider } from "@recobra/ui/tooltip";

import { NavHeaderProvider, NavHeaderSlot } from "../layout";
import { useAuth } from "../lib";
import { resolveUserDisplayName } from "../lib";
import { applyTheme, useUserPreferences } from "../lib";
import { NotificationBell, NotificationEventInvalidation } from "../notifications";
import { AppSidebar } from "./AppSidebar";
import { AuthFallback } from "./AuthFallback";
import { CreationActions } from "./CreationActions";
import { CreationEventInvalidation } from "./CreationEventInvalidation";
import { DeploymentRefreshDialog } from "./DeploymentRefreshDialog";
import { PageContentFallback } from "./PageContentFallback";

export const AppLayout: FC = () => (
  <Suspense fallback={<AuthFallback />}>
    <AppLayoutContent />
  </Suspense>
);

const AppLayoutContent: FC = () => {
  const { user, displayName, email, picture, cognitoGroups, clientIds, branchIds, signOut } =
    useAuth();
  const ability = useMemo(
    () =>
      defineAbilityFor({
        groups: cognitoGroups,
        userId: user.userId,
        username: user.username,
        clientIds,
        branchIds,
      }),
    [branchIds, clientIds, cognitoGroups, user.userId, user.username],
  );
  const { preferences } = useUserPreferences();

  useLayoutEffect(() => applyTheme(preferences.theme), [preferences.theme]);

  return (
    <AbilityProvider value={ability}>
      <TooltipProvider>
        <CreationEventInvalidation user={{ userId: user.userId, username: user.username }} />
        <NotificationEventInvalidation userId={user.userId} />
        <DeploymentRefreshDialog />
        <SidebarProvider>
          <AppSidebar
            cognitoGroups={cognitoGroups}
            userName={resolveUserDisplayName({
              name: displayName,
              loginId: user.signInDetails?.loginId,
              email,
              username: user.username,
            })}
            userEmail={email}
            userAvatar={picture}
            onSignOut={() => {
              void signOut();
            }}
          />
          <NavHeaderProvider>
            <SidebarInset className="h-svh max-h-svh overflow-hidden">
              <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
                <SidebarTrigger className="-ml-1" />
                <Separator orientation="vertical" className="data-vertical:min-h-4" />
                <NavHeaderSlot />
                <div className="ml-auto flex items-center gap-2">
                  <CreationActions />
                  <NotificationBell />
                </div>
              </header>
              <main className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto p-4">
                <Suspense fallback={<PageContentFallback />}>
                  <Outlet />
                </Suspense>
              </main>
            </SidebarInset>
          </NavHeaderProvider>
        </SidebarProvider>
      </TooltipProvider>
    </AbilityProvider>
  );
};
